import { Text, TextProps, Tooltip } from '@mantine/core';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { calculateWidthUsingTextStyles } from '@dealstack/frontend/app/pages/Valuations/shared/textMeasurement.utils';
import { truncateText } from '@dealstack/frontend/app/pages/Valuations/components/EntitySelectorDropdown/TruncateMiddle/TruncateMiddle.utils';

interface Props extends TextProps {
	text: string;
}

export const TruncateMiddle = ({ text, ...textProps }: Props) => {
	const [truncatedText, setTruncatedText] = useState(text);
	const targetRef = useRef<HTMLDivElement>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);
	const lastWidthRef = useRef<number>(0);
	const lastTextRef = useRef<string>(text);

	/*
	 * The function calculates the current target ref's width and compares it with the previous width value.
	 * If the target ref does not exist or the current width matches the previous value, the function returns undefined.
	 * Otherwise the new width is returned.
	 */
	const computeTargetWidthIfChanged = useCallback((): number | undefined => {
		if (!targetRef.current) {
			return undefined;
		}

		// need to ceil the value because of subpixel rendering
		const width = Math.ceil(targetRef.current.getBoundingClientRect().width);

		if (width > 0 && Math.abs(width - lastWidthRef.current) > 0) {
			lastWidthRef.current = width;
			return width;
		}

		return undefined;
	}, []);

	/*
	 * The function returns the text to render based on the width argument.
	 * If the text fits, the original text is returned.
	 * Otherwise, the text is truncated and the number of characters on each side is decided using binary search.
	 */
	const getTextToRender = useCallback(
		(width: number) => {
			if (!targetRef.current || width <= 0) {
				return text;
			}

			const fullTextWidth = calculateWidthUsingTextStyles(text, targetRef.current);

			// Return original text if it fits
			if (fullTextWidth <= width) {
				return text;
			}

			// Using binary search to decide the optimal number of chars to show on each side.
			const maxCharOnOneSide = Math.floor(text.length / 2);
			let l = 0,
				r = maxCharOnOneSide;
			while (l < r) {
				const m = Math.ceil((l + r) / 2);
				const shortenedText = truncateText(text, m);
				const shortenedWidth = calculateWidthUsingTextStyles(
					shortenedText,
					targetRef.current
				);
				if (shortenedWidth > width) {
					r = m - 1;
				} else {
					l = m;
				}
			}

			return truncateText(text, l);
		},
		[text]
	);

	useLayoutEffect(() => {
		if (!targetRef.current) {
			return;
		}

		const handleResize = () => {
			const width = computeTargetWidthIfChanged();
			if (width) {
				setTruncatedText(getTextToRender(width));
			}

			if (text !== lastTextRef.current) {
				lastTextRef.current = text;
				setTruncatedText(getTextToRender(0));
			}
		};

		handleResize();

		resizeObserverRef.current = new ResizeObserver(() => {
			handleResize();
		});
		resizeObserverRef.current.observe(targetRef.current);

		return () => {
			if (resizeObserverRef.current) {
				resizeObserverRef.current.disconnect();
			}
		};
	}, [text, computeTargetWidthIfChanged, getTextToRender]);

	const showTooltip = truncatedText !== text;

	return (
		<Tooltip
			label={text}
			events={{ hover: showTooltip, focus: showTooltip, touch: showTooltip }}
			withinPortal
		>
			<Text
				ref={targetRef}
				sx={{
					width: '100%',
					minWidth: 0,
					maxWidth: '100%',
					display: 'block',
					overflow: 'hidden',
				}}
				{...textProps}
			>
				<div style={{ width: '100%', minWidth: 0, textWrap: 'nowrap' }}>
					{truncatedText}
				</div>
			</Text>
		</Tooltip>
	);
};
